<template>
  <b-modal v-model="visible" size="lg" centered hide-footer no-close-on-backdrop :no-close-on-esc="downloading" body-class="p-0" @hidden="reset">
    <template #modal-title>
      <span class="ixm-title"><i class="fas fa-file-excel"/>{{ msg('invoiceExport.title') }}</span>
    </template>

    <div class="ixm">
      <!-- report type -->
      <section class="ixm-block">
        <label class="ixm-label">{{ msg('invoiceExport.type') }}</label>
        <div class="ixm-types">
          <button v-for="t in types" :key="t.key" type="button" class="ixm-type" :class="{'ixm-type--on': type === t.key}" @click="type = t.key">
            <span class="ixm-type__icon"><i :class="t.icon"/></span>
            <span class="ixm-type__text">
              <b>{{ msg('invoiceExport.' + t.key) }}</b>
              <small>{{ msg('invoiceExport.' + t.key + 'Hint') }}</small>
            </span>
            <i class="ixm-type__check fas fa-circle-check"/>
          </button>
        </div>
      </section>

      <!-- scope -->
      <section class="ixm-block ixm-grid2">
        <div>
          <label class="ixm-label">{{ msg('invoiceExport.building') }}</label>
          <v-select v-model="buildingId" :options="buildings" label="address" :reduce="b => b.id" :clearable="false"
                    :placeholder="msg('invoiceExport.buildingPlaceholder')" :dir="isRTL ? 'rtl' : 'ltr'" append-to-body/>
        </div>
        <div>
          <label class="ixm-label">{{ msg('invoiceExport.platform') }}</label>
          <div class="ixm-chips">
            <button v-for="p in platforms" :key="p.value || 'all'" type="button" class="ixm-chip" :class="{'ixm-chip--on': platform === p.value}" @click="platform = p.value">
              {{ p.label }}
            </button>
          </div>
        </div>
      </section>

      <!-- period -->
      <section class="ixm-block">
        <label class="ixm-label">{{ msg('invoiceExport.period') }} <small class="ixm-hint">({{ msg('invoiceExport.periodHint') }})</small></label>
        <div class="ixm-chips">
          <button v-for="p in presets" :key="p" type="button" class="ixm-chip" :class="{'ixm-chip--on': preset === p}" @click="applyPreset(p)">
            {{ msg('invoiceExport.presets.' + p) }}
          </button>
        </div>
        <div class="ixm-range">
          <div class="ixm-range__field">
            <span class="ixm-range__lbl">{{ msg('invoiceExport.from') }}</span>
            <flat-pickr v-model="start" class="form-control" :config="dateConfig" @on-change="preset = 'custom'"/>
          </div>
          <span class="ixm-range__arrow"><i class="fas fa-arrow-right-long"/></span>
          <div class="ixm-range__field">
            <span class="ixm-range__lbl">{{ msg('invoiceExport.to') }}</span>
            <flat-pickr v-model="end" class="form-control" :config="dateConfig" @on-change="preset = 'custom'"/>
          </div>
          <span v-if="days != null" class="ixm-range__days tabular">{{ days }} {{ msg('invoiceExport.days') }}</span>
        </div>
      </section>

      <!-- what you get -->
      <section class="ixm-block ixm-contents">
        <i class="fas fa-table-list"/>
        <span>{{ msg('invoiceExport.contents.' + type) }}</span>
      </section>

      <!-- footer -->
      <div class="ixm-foot">
        <span v-if="error" class="ixm-error"><i class="fas fa-triangle-exclamation mr-50"/>{{ error }}</span>
        <span v-else-if="!valid" class="ixm-hint">{{ validationHint }}</span>
        <span class="ixm-foot__spacer"/>
        <b-button variant="flat-secondary" :disabled="downloading" @click="visible = false">{{ msg('Close') }}</b-button>
        <b-button variant="success" class="ixm-download" :disabled="!valid || downloading" @click="download">
          <b-spinner v-if="downloading" small class="mr-50"/><i v-else class="fas fa-download mr-50"/>
          {{ downloading ? msg('invoiceExport.downloading') : msg('invoiceExport.download') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {mapActions} from 'vuex'
import flatPickr from 'vue-flatpickr-component'

const PRESETS = ['thisMonth', 'lastMonth', 'thisQuarter', 'lastQuarter', 'thisYear', 'lastYear', 'last30', 'custom'];

/**
 * One dialog for both invoice Excel reports: report type, building, platform,
 * period (presets or free dates), then a real download through the API
 * (Authorization header, blob) — no more popup window with the token in the URL.
 */
export default {
  name: 'InvoiceExportModal',
  components: {flatPickr},
  props: {
    buildings: {type: Array, default: () => []},
  },
  data() {
    return {
      visible: false,
      type: 'invoices',
      buildingId: null,
      platform: null,
      preset: 'thisMonth',
      start: this.$moment().startOf('month').format('YYYY-MM-DD'),
      end: this.$moment().format('YYYY-MM-DD'),
      downloading: false,
      error: null,
      presets: PRESETS,
      dateConfig: {dateFormat: 'Y-m-d', altInput: true, altFormat: 'd.m.Y', allowInput: true},
    }
  },
  computed: {
    types() {
      return [
        {key: 'invoices', icon: 'fas fa-file-invoice'},
        {key: 'regions', icon: 'fas fa-earth-europe'},
      ];
    },
    platforms() {
      return [
        {value: null, label: this.msg('invoiceExport.allPlatforms')},
        {value: 'Booking', label: 'Booking'}, {value: 'Airbnb', label: 'Airbnb'}, {value: 'Expedia', label: 'Expedia'},
        {value: 'Vrbo', label: 'Vrbo'}, {value: 'Agoda', label: 'Agoda'}, {value: 'Treasure Home', label: 'Treasure Home'},
      ];
    },
    days() {
      if (!this.start || !this.end) return null;
      const d = this.$moment(this.end).diff(this.$moment(this.start), 'days');
      return d >= 0 ? d + 1 : null;
    },
    valid() {
      return this.buildingId != null && !!this.start && !!this.end && this.days != null;
    },
    validationHint() {
      if (this.buildingId == null) return this.msg('invoiceExport.noBuilding');
      if (!this.start || !this.end || this.days == null) return this.msg('invoiceExport.invalidRange');
      return '';
    },
  },
  methods: {
    ...mapActions('monetary', ['downloadMonetaryExcel']),
    open(type) {
      if (type) this.type = type;
      this.error = null;
      if (this.buildingId == null && this.buildings.length === 1) this.buildingId = this.buildings[0].id;
      this.visible = true;
    },
    reset() {
      this.error = null;
      this.downloading = false;
    },
    applyPreset(p) {
      this.preset = p;
      const m = this.$moment;
      const f = d => d.format('YYYY-MM-DD');
      switch (p) {
        case 'thisMonth': this.start = f(m().startOf('month')); this.end = f(m()); break;
        case 'lastMonth': this.start = f(m().subtract(1, 'month').startOf('month')); this.end = f(m().subtract(1, 'month').endOf('month')); break;
        case 'thisQuarter': this.start = f(m().startOf('quarter')); this.end = f(m()); break;
        case 'lastQuarter': this.start = f(m().subtract(1, 'quarter').startOf('quarter')); this.end = f(m().subtract(1, 'quarter').endOf('quarter')); break;
        case 'thisYear': this.start = f(m().startOf('year')); this.end = f(m()); break;
        case 'lastYear': this.start = f(m().subtract(1, 'year').startOf('year')); this.end = f(m().subtract(1, 'year').endOf('year')); break;
        case 'last30': this.start = f(m().subtract(29, 'days')); this.end = f(m()); break;
        default:
      }
    },
    download() {
      if (!this.valid) return;
      this.downloading = true;
      this.error = null;
      const building = this.buildings.find(b => b.id === this.buildingId);
      const slug = String(building && building.address ? building.address : 'building').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
      const filename = `${this.type === 'invoices' ? 'invoice-overview' : 'region-statistics'}_${slug}_${this.start}_${this.end}.xlsx`;
      this.downloadMonetaryExcel({type: this.type, buildingId: this.buildingId, platform: this.platform, start: this.start, end: this.end})
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => window.URL.revokeObjectURL(url), 2000);
            this.$store.dispatch('notifications/success', {title: {text: 'Excel'}, message: {text: '%s downloaded', arguments: [filename]}});
          })
          .catch(error => this.error = error)
          .finally(() => this.downloading = false);
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.ixm-title { display: inline-flex; align-items: center; gap: 8px; font-weight: 800; i { color: #1d6f42; font-size: 1.1rem; } }
.ixm { padding: 16px 18px 0; display: flex; flex-direction: column; gap: 16px; }
.ixm-block { display: flex; flex-direction: column; gap: 8px; }
.ixm-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.ixm-label { display: flex; align-items: center; gap: 6px; margin: 0; font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; }
.ixm-hint { font-size: .7rem; color: #94a3b8; text-transform: none; letter-spacing: 0; font-weight: 500; }
.ixm-error { font-size: .78rem; color: #ea5455; font-weight: 600; }

.ixm-types { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.ixm-type {
  position: relative; display: flex; align-items: center; gap: 12px; text-align: left; cursor: pointer;
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 14px; background: #fff; padding: 12px 14px; transition: all .15s;
  &:hover { border-color: rgba(29, 111, 66, .5); }
  &:focus { outline: none; }
  &--on { border-color: #1d6f42; background: rgba(29, 111, 66, .06); box-shadow: 0 0 0 2px rgba(29, 111, 66, .15); .ixm-type__check { opacity: 1; } .ixm-type__icon { background: #1d6f42; color: #fff; } }
}
.ixm-type__icon { width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, .05); color: #64748b; font-size: 1rem; transition: all .15s; }
.ixm-type__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; b { font-size: .86rem; color: #1f2a44; } small { font-size: .7rem; color: #64748b; line-height: 1.35; } }
.ixm-type__check { position: absolute; top: 10px; right: 12px; color: #1d6f42; opacity: 0; transition: opacity .15s; }

.ixm-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ixm-chip {
  border: 1px solid rgba(15, 23, 42, .12); border-radius: 999px; background: #fff; cursor: pointer;
  font-size: .72rem; font-weight: 700; color: #64748b; padding: 5px 12px; transition: all .15s;
  &:hover { border-color: rgba(29, 111, 66, .5); color: #1d6f42; }
  &:focus { outline: none; }
  &--on { background: #1d6f42; border-color: #1d6f42; color: #fff; }
}
.ixm-range { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
.ixm-range__field { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 150px; }
.ixm-range__lbl { font-size: .64rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .05em; }
.ixm-range__arrow { color: #94a3b8; padding-bottom: 12px; }
.ixm-range__days { font-size: .74rem; font-weight: 700; color: #1d6f42; padding-bottom: 11px; white-space: nowrap; }
.ixm-contents { flex-direction: row; align-items: flex-start; gap: 10px; font-size: .76rem; color: #475569; background: rgba(29, 111, 66, .06); border-radius: 12px; padding: 10px 12px; i { color: #1d6f42; margin-top: 2px; } }
.ixm-foot { display: flex; align-items: center; gap: 8px; padding: 12px 0 16px; border-top: 1px solid rgba(15, 23, 42, .06); flex-wrap: wrap; }
.ixm-foot__spacer { flex: 1; }
.ixm-download { background: #1d6f42 !important; border-color: #1d6f42 !important; font-weight: 700; box-shadow: 0 6px 14px rgba(29, 111, 66, .25); }
@media (max-width: 640px) { .ixm-grid2, .ixm-types { grid-template-columns: 1fr; } }
</style>

<style lang="scss">
.dark-layout {
  .ixm-type { background: #283046; border-color: rgba(255, 255, 255, .12); b { color: #e9ecf2; } small { color: #94a3b8; } }
  .ixm-type--on { background: rgba(29, 111, 66, .18); }
  .ixm-type__icon { background: rgba(255, 255, 255, .08); color: #94a3b8; }
  .ixm-chip { background: #283046; border-color: rgba(255, 255, 255, .16); color: #b4b7bd; }
  .ixm-chip--on { background: #1d6f42; border-color: #1d6f42; color: #fff; }
  .ixm-contents { color: #b4b7bd; background: rgba(29, 111, 66, .14); }
  .ixm-foot { border-color: rgba(255, 255, 255, .08); }
}
</style>
